import React from 'react';
import { Link } from 'react-router-dom';
import { 
  CheckCircle, 
  ChevronRight,
  Heart,
  Shield,
  Star,
  Award,
  Users,
  Clock,
  MapPin,
  Phone
} from 'lucide-react';

/**
 * Card Section Component
 * Reusable card section matching the style of Specialty Care and General Practice sections
 * Features: Title, Subtitle, Multiple Cards, CTA Button, Theme/White Background
 */
const CardSectionComponent = ({ config = {} }) => {
  const primaryColor = 'var(--theme-primary)';
  const primaryLight = 'var(--theme-primary-light)';

  // Configuration with defaults
  const title = config.title || 'Our Services';
  const subtitle = config.subtitle || 'Comprehensive care for your beloved pets';
  const backgroundStyle = config.background_style || 'white'; // 'theme' or 'white'
  const cards = config.cards || [];
  
  // Button configuration
  const showButton = config.show_button !== false;
  const buttonText = config.button_text || 'View All Services';
  const buttonLink = config.button_link || '#';

  // Icon mapping
  const iconMap = {
    'check-circle': CheckCircle,
    'heart': Heart,
    'shield': Shield,
    'star': Star,
    'award': Award,
    'users': Users,
    'clock': Clock,
    'map-pin': MapPin,
    'phone': Phone,
  };

  // Background styles
  const isThemeBackground = backgroundStyle === 'theme';
  const sectionClass = isThemeBackground 
    ? 'p-6 md:p-8' 
    : 'bg-gray-50 py-8';
  
  const sectionStyle = isThemeBackground 
    ? { backgroundColor: primaryColor } 
    : {};

  const titleColor = isThemeBackground ? 'text-white' : 'text-gray-900';
  const subtitleColor = isThemeBackground ? 'text-white/90' : 'text-gray-600';
  const decorLineClass = isThemeBackground ? 'bg-white/30' : 'bg-gray-300';
  const titleBgClass = isThemeBackground 
    ? 'bg-white/10 backdrop-blur-sm border-white/20' 
    : 'bg-white backdrop-blur-sm border-gray-200';

  return (
    <section className={sectionClass} style={sectionStyle}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center mb-4">
            <div className={`h-px ${decorLineClass} flex-grow max-w-24`}></div>
            <div className={`mx-4 px-6 py-2 ${titleBgClass} rounded-full border shadow-sm`}>
              <h2 className={`font-bold ${titleColor} text-lg tracking-wide`}>
                {title}
              </h2>
            </div>
            <div className={`h-px ${decorLineClass} flex-grow max-w-24`}></div>
          </div>
          {subtitle && (
            <p className={`${subtitleColor} max-w-3xl mx-auto`} style={{ fontSize: '1rem' }}>
              {subtitle}
            </p>
          )}
        </div>

        {/* Cards Grid */}
        {cards.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {cards.map((card, index) => {
              const IconComponent = iconMap[card.icon] || CheckCircle;
              const cardIconColor = card.icon_color || primaryColor;
              const cardIconBg = card.icon_bg || primaryLight;

              return (
                <div 
                  key={index}
                  className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-200 hover:-translate-y-1"
                >
                  {/* Icon */}
                  <div 
                    className="flex items-center justify-center w-16 h-16 rounded-full mb-6" 
                    style={{ backgroundColor: cardIconBg }}
                  >
                    <IconComponent className="h-8 w-8" style={{ color: cardIconColor }} />
                  </div>

                  {/* Card Title */}
                  <h3 className="font-bold text-gray-900 mb-3" style={{ fontSize: '1rem' }}>
                    {card.title || 'Card Title'}
                  </h3>

                  {/* Card Description */}
                  <p className="text-gray-600 mb-4">
                    {card.description || 'Card description goes here'}
                  </p>

                  {/* Card Link (optional) */}
                  {card.link && card.link_text && (
                    <Link
                      to={card.link}
                      className="inline-flex items-center font-semibold transition-colors"
                      style={{ color: cardIconColor }}
                      onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                      onMouseLeave={(e) => e.target.style.opacity = '1'}
                    >
                      {card.link_text}
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* CTA Button */}
        {showButton && (
          <div className="text-center mt-6">
            <Link
              to={buttonLink}
              className={`inline-flex items-center justify-center px-6 py-2.5 font-semibold rounded-lg transition-colors duration-200 shadow-lg text-sm ${
                isThemeBackground 
                  ? 'bg-white text-gray-900 hover:bg-gray-100'
                  : 'text-white hover:opacity-90'
              }`}
              style={!isThemeBackground ? { backgroundColor: primaryColor } : {}}
            >
              {buttonText}
              <ChevronRight className="ml-2 h-4 w-4" />
            </Link>
          </div>
        )}

        {/* Empty state when no cards */}
        {cards.length === 0 && (
          <div className="text-center py-8">
            <p className={`${subtitleColor}`}>
              No cards added yet. Edit this component to add cards.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default CardSectionComponent;
